import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Trophy, Edit, Save, X, Lock, Plus, Crown } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompetitionSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingEntry, setIsAddingEntry] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Form fields
  const [formQuestion, setFormQuestion] = useState('');
  const [formPrize, setFormPrize] = useState('');
  const [formAnswer, setFormAnswer] = useState('');
  const [formClosing, setFormClosing] = useState('');
  const [entryName, setEntryName] = useState('');
  const [entryAnswer, setEntryAnswer] = useState('');

  const fetchCompetition = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/competition`);
      setData(response.data);
      if (response.data.question) {
        setFormQuestion(response.data.question);
        setFormPrize(response.data.prize);
        setFormAnswer(response.data.correct_answer || '');
        setFormClosing(response.data.closing_date || '');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching competition:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompetition();
  }, [fetchCompetition]);

  const handleEditClick = useCallback(() => {
    setPendingAction('edit');
    setShowPasswordPrompt(true);
  }, []);

  const handleAddEntryClick = useCallback(() => {
    setPendingAction('addEntry');
    setShowPasswordPrompt(true);
  }, []);

  const handlePasswordSubmit = useCallback(async () => {
    if (password !== 'editor') {
      toast.error('Invalid password');
      setPassword('');
      return;
    }

    if (pendingAction === 'edit') {
      setIsEditing(true);
      setShowPasswordPrompt(false);
    } else if (pendingAction === 'addEntry') {
      setIsAddingEntry(true);
      setShowPasswordPrompt(false);
    } else if (pendingAction === 'save') {
      try {
        await axios.post(`${API}/competition`, null, {
          params: {
            password: 'editor',
            question: formQuestion,
            prize: formPrize,
            correct_answer: formAnswer,
            closing_date: formClosing
          }
        });
        toast.success('Competition saved!');
        setIsEditing(false);
        fetchCompetition();
      } catch (error) {
        toast.error('Failed to save');
      }
    } else if (pendingAction === 'saveEntry') {
      try {
        await axios.post(`${API}/competition/entry`, null, {
          params: { password: 'editor', name: entryName, answer: entryAnswer }
        });
        toast.success('Entry added!');
        setIsAddingEntry(false);
        setEntryName('');
        setEntryAnswer('');
        fetchCompetition();
      } catch (error) {
        toast.error('Failed to add entry');
      }
    } else if (pendingAction?.startsWith('winner_')) {
      const idx = parseInt(pendingAction.split('_')[1]);
      try {
        await axios.post(`${API}/competition/winner`, null, {
          params: { password: 'editor', entry_index: idx }
        });
        toast.success('Winner marked!');
        fetchCompetition();
      } catch (error) {
        toast.error('Failed to mark winner');
      }
    }
    
    setPassword('');
    setPendingAction(null);
    setShowPasswordPrompt(false);
  }, [password, pendingAction, formQuestion, formPrize, formAnswer, formClosing, entryName, entryAnswer, fetchCompetition]);

  const handleSave = useCallback(() => {
    setPendingAction('save');
    setShowPasswordPrompt(true);
  }, []);

  const handleSaveEntry = useCallback(() => {
    if (!entryName || !entryAnswer) {
      toast.error('Please fill in name and answer');
      return;
    }
    setPendingAction('saveEntry');
    setShowPasswordPrompt(true);
  }, [entryName, entryAnswer]);

  const handleMarkWinner = useCallback((index) => {
    setPendingAction(`winner_${index}`);
    setShowPasswordPrompt(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setIsAddingEntry(false);
    setShowPasswordPrompt(false);
    setPassword('');
    setPendingAction(null);
  }, []);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  }, []);

  return (
    <div className="content-card content-card-large" data-testid="competition-section">
      <div className="card-header">
        <h2>
          <Trophy size={20} className="inline" style={{ color: '#FFD700' }} /> Latest Competition
        </h2>
        {!isEditing && !showPasswordPrompt && (
          <Button onClick={handleEditClick} variant="outline" size="sm" title="Edit competition">
            <Edit size={16} />
          </Button>
        )}
      </div>

      {showPasswordPrompt && (
        <div className="password-prompt">
          <div className="password-input-container">
            <Lock size={18} />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              placeholder="Enter editor password"
              className="password-input"
              autoFocus
            />
            <Button onClick={handlePasswordSubmit} size="sm">Unlock</Button>
            <Button onClick={handleCancel} variant="ghost" size="sm"><X size={16} /></Button>
          </div>
        </div>
      )}

      <ScrollArea className="card-content">
        {loading ? (
          <p className="loading-text">Loading competition...</p>
        ) : isEditing ? (
          <div className="edit-mode">
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#FFC107', fontSize: '0.9rem' }}>Question:</label>
              <textarea
                value={formQuestion}
                onChange={(e) => setFormQuestion(e.target.value)}
                placeholder="Competition question..."
                rows={2}
                style={{ width: '100%', padding: '0.5rem', backgroundColor: '#fff', color: '#000', marginTop: '0.25rem' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#FFC107', fontSize: '0.9rem' }}>Prize:</label>
              <input
                type="text"
                value={formPrize}
                onChange={(e) => setFormPrize(e.target.value)}
                placeholder="What can they win?"
                style={{ width: '100%', padding: '0.5rem', backgroundColor: '#fff', color: '#000', marginTop: '0.25rem' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#FFC107', fontSize: '0.9rem' }}>Correct Answer (hidden until closed):</label>
              <input
                type="text"
                value={formAnswer}
                onChange={(e) => setFormAnswer(e.target.value)}
                placeholder="The correct answer"
                style={{ width: '100%', padding: '0.5rem', backgroundColor: '#fff', color: '#000', marginTop: '0.25rem' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#FFC107', fontSize: '0.9rem' }}>Closing Date:</label>
              <input
                type="datetime-local"
                value={formClosing}
                onChange={(e) => setFormClosing(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', backgroundColor: '#fff', color: '#000', marginTop: '0.25rem' }}
              />
            </div>
            <div className="edit-actions">
              <Button onClick={handleSave} className="save-btn"><Save size={16} /> Save</Button>
              <Button onClick={handleCancel} variant="outline"><X size={16} /> Cancel</Button>
            </div>
          </div>
        ) : !data?.question ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
            <Trophy size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p>No active competition</p>
            <Button onClick={handleEditClick} variant="outline" size="sm" style={{ marginTop: '1rem' }}>
              <Plus size={16} /> Create Competition
            </Button>
          </div>
        ) : (
          <div className="updates-content">
            <div style={{ padding: '1rem', background: 'rgba(255,215,0,0.1)', borderRadius: '8px', marginBottom: '1rem' }}>
              <strong style={{ color: '#FFD700' }}>Q:</strong> {data.question}
            </div>
            
            <div style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: '#25D366' }}>Prize:</strong> {data.prize}
            </div>
            
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>Closes:</strong> {formatDate(data.closing_date)}
              {data.is_closed && <span style={{ marginLeft: '0.5rem', background: '#666', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem' }}>CLOSED</span>}
            </div>

            {data.is_closed && data.correct_answer && (
              <div style={{ marginBottom: '0.75rem', color: '#25D366' }}>
                <strong>Answer:</strong> {data.correct_answer}
              </div>
            )}

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem', marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong style={{ color: '#FFC107' }}>Entries ({data.total_entries})</strong>
                {!data.is_closed && (
                  <Button onClick={handleAddEntryClick} variant="outline" size="sm">
                    <Plus size={14} /> Add Entry
                  </Button>
                )}
              </div>

              {isAddingEntry && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={entryName}
                    onChange={(e) => setEntryName(e.target.value)}
                    placeholder="Caller name"
                    style={{ flex: 1, minWidth: '120px', padding: '0.5rem', backgroundColor: '#fff', color: '#000' }}
                  />
                  <input
                    type="text"
                    value={entryAnswer}
                    onChange={(e) => setEntryAnswer(e.target.value)}
                    placeholder="Their answer"
                    style={{ flex: 1, minWidth: '120px', padding: '0.5rem', backgroundColor: '#fff', color: '#000' }}
                  />
                  <Button onClick={handleSaveEntry} size="sm">Add</Button>
                  <Button onClick={() => setIsAddingEntry(false)} variant="ghost" size="sm"><X size={14} /></Button>
                </div>
              )}

              {data.entries?.length > 0 ? (
                <div>
                  {data.entries.map((entry, idx) => (
                    <div
                      key={`${entry.name}-${entry.timestamp || idx}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        background: entry.is_winner ? 'rgba(255,215,0,0.2)' : (idx % 2 === 0 ? '#f5f5f5' : '#fff'),
                        borderRadius: '6px',
                        border: entry.is_winner ? '2px solid #FFD700' : '1px solid #ddd'
                      }}
                    >
                      <div>
                        <strong style={{ color: '#000' }}>
                          {entry.is_winner && <Crown size={14} style={{ color: '#FFD700', marginRight: '4px' }} />}
                          {entry.name}
                        </strong>
                        <span style={{ color: '#333', marginLeft: '0.5rem' }}>— {entry.answer}</span>
                      </div>
                      {data.is_closed && !entry.is_winner && (
                        <Button onClick={() => handleMarkWinner(idx)} variant="ghost" size="sm" title="Mark as winner">
                          <Crown size={14} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>No entries yet</p>
              )}
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default CompetitionSection;
